import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import type {
  TemplateResponse,
  TemplateWithLayersResponse,
  FrontendTemplate,
  CanvasPreset,
} from '@/lib/api-types'
import { mapTemplateResponseToFrontend } from '@/lib/api-types'
import { useToast } from '@/hooks/use-toast'

export interface UseTemplatesResult {
  templates: TemplateResponse[]
  loading: boolean
  error: string | null
  presets: CanvasPreset[]
  getTemplateWithLayers: (id: string) => Promise<TemplateWithLayersResponse | null>
  createTemplate: (
    name: string,
    width: number,
    height: number
  ) => Promise<TemplateResponse | null>
  updateTemplate: (
    id: string,
    name: string
  ) => Promise<TemplateResponse | null>
  deleteTemplate: (id: string) => Promise<boolean>
  deleteAllTemplates: () => Promise<boolean>
  refetch: () => Promise<void>
}

export function useTemplates(): UseTemplatesResult {
  const [templates, setTemplates] = useState<TemplateResponse[]>([])
  const [presets, setPresets] = useState<CanvasPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.listTemplates(0, 100)
      setTemplates(response.data)
    } catch (err: any) {
      const msg =
        err.response?.data?.detail ||
        err.message ||
        'Erro ao carregar templates'
      setError(msg)
      toast({
        title: 'Erro',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  const fetchPresets = useCallback(async () => {
    try {
      const response = await apiClient.getCanvasPresets()
      setPresets(response.data.presets)
    } catch (err: any) {
      console.error('Erro ao carregar presets:', err)
      // Não mostrar toast para presets (não é crítico)
    }
  }, [])

  useEffect(() => {
    fetchTemplates()
    fetchPresets()
  }, [fetchTemplates, fetchPresets])

  const getTemplateWithLayers = useCallback(
    async (id: string): Promise<TemplateWithLayersResponse | null> => {
      try {
        const response = await apiClient.getTemplate(id)
        return response.data
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao carregar template'
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      }
    },
    [toast]
  )

  const createTemplate = useCallback(
    async (
      name: string,
      width: number,
      height: number
    ): Promise<TemplateResponse | null> => {
      try {
        const response = await apiClient.createTemplate({
          name,
          canvas_width: width,
          canvas_height: height,
        })
        setTemplates((prev) => [...prev, response.data])
        toast({
          title: 'Sucesso',
          description: 'Template criado com sucesso',
        })
        return response.data
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao criar template'
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      }
    },
    [toast]
  )

  const updateTemplate = useCallback(
    async (
      id: string,
      name: string
    ): Promise<TemplateResponse | null> => {
      try {
        const response = await apiClient.updateTemplate(id, {
          name,
        })
        setTemplates((prev) =>
          prev.map((t) => (t.id === id ? response.data : t))
        )
        toast({
          title: 'Sucesso',
          description: 'Template atualizado com sucesso',
        })
        return response.data
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao atualizar template'
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      }
    },
    [toast]
  )

  const deleteTemplate = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await apiClient.deleteTemplate(id)
        setTemplates((prev) => prev.filter((t) => t.id !== id))
        toast({
          title: 'Sucesso',
          description: 'Template deletado',
        })
        return true
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao deletar template'
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return false
      }
    },
    [toast]
  )

  const deleteAllTemplates = useCallback(
    async (): Promise<boolean> => {
      try {
        // Deletar cada template
        await Promise.all(templates.map((t) => apiClient.deleteTemplate(t.id)))
        setTemplates([])
        toast({
          title: 'Sucesso',
          description: 'Todos os templates foram deletados',
        })
        return true
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao deletar templates'
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return false
      }
    },
    [templates, toast]
  )

  return {
    templates,
    loading,
    error,
    presets,
    getTemplateWithLayers,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    deleteAllTemplates,
    refetch: fetchTemplates,
  }
}


